import React, { useRef } from "react"
import { Button, Segmented, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faDatabase,
  faFilter,
  faFileExport,
  faFloppyDisk,
} from "@fortawesome/free-solid-svg-icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import {
  DashboardMode,
  selectIsDirty,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import DummyBadge from "features/DummyData/components/DummyBadge.component"
import { useQuestionnaireProjectId } from "features/DummyData/api/useDummyData"
import LayoutControls from "./LayoutControls.component"
import DataPanel from "./DataPanel.component"

interface ActionRowProps {
  onSave: () => void
  onExport: () => void
  onOpenFilters: () => void
  isSaving: boolean
  /** More actions, placed before Export (the assistant's toggle). */
  extraActions?: React.ReactNode
}

const MODE_OPTIONS = [
  { label: "Build", value: "build" },
  { label: "View", value: "view" },
]

/**
 * Build mode's top row, one line that never wraps: the editable title over
 * a saved-state hairline (accent while there are unsaved changes), the
 * Build / View switch, the filter definitions and the Data panel, then
 * "Layout ▾" and full screen, any extra actions, Export and Save, the one solid button, on the
 * right. A "Dummy data" badge follows the title while the project's data is
 * dummy data.
 */
const ActionRow: React.FC<ActionRowProps> = ({
  onSave,
  onExport,
  onOpenFilters,
  isSaving,
  extraActions,
}) => {
  const title = useDashboardStore((s) => s.document.title)
  const setTitle = useDashboardStore((s) => s.setTitle)
  const mode = useDashboardStore((s) => s.mode)
  const setMode = useDashboardStore((s) => s.setMode)
  const filterCount = useDashboardStore((s) => s.document.filters.length)
  const dirty = useDashboardStore(selectIsDirty)
  const setDataPanelOpen = useDashboardStore((s) => s.setDataPanelOpen)
  // A project without a questionnaire never has dummy data: nothing to read.
  const projectId = useDashboardStore((s) => s.projectId)
  const dummyFor = useQuestionnaireProjectId(projectId)
  const rowRef = useRef<HTMLDivElement>(null)
  // The drawer renders inside the workspace, like the rails' drawers, so it
  // gets the --qc-* tokens and stays inside the full screen overlay.
  const panelContainer = (): HTMLElement =>
    rowRef.current?.closest<HTMLElement>(".dashboard-root") ?? document.body

  return (
    <div
      ref={rowRef}
      className="dq-action-row flex-nowrap"
      role="toolbar"
      aria-label="Dashboard actions"
    >
      <div className="dq-title-block">
        <input
          className="dq-title-input"
          aria-label="Dashboard title"
          // The whole title on hover, when it is cut short.
          title={title}
          value={title}
          size={Math.min(Math.max(title.length + 1, 12), 48)}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div
          className="dq-saved-bar"
          data-dirty={dirty}
          title={dirty ? "Unsaved changes" : "Saved"}
        />
        <span className="sr-only" aria-live="polite">
          {dirty ? "Unsaved changes" : ""}
        </span>
      </div>
      <DummyBadge projectId={dummyFor} />
      <Segmented
        value={mode}
        onChange={(v) => setMode(v as DashboardMode)}
        options={MODE_OPTIONS}
        aria-label="Mode"
      />
      <Tooltip
        title={
          filterCount > 0
            ? `Filters viewers can use (${filterCount})`
            : "Add filters viewers can use"
        }
        placement="bottom"
      >
        <Button
          aria-label="Filters"
          icon={<FontAwesomeIcon icon={faFilter} />}
          onClick={onOpenFilters}
        />
      </Tooltip>
      <Tooltip
        title="Data: pipelines, weighting, colours and table style"
        placement="bottom"
      >
        <Button
          aria-label="Data panel"
          icon={<FontAwesomeIcon icon={faDatabase} />}
          onClick={() => setDataPanelOpen(true)}
        />
      </Tooltip>
      <DataPanel getContainer={panelContainer} />
      <div className="ml-auto flex flex-none items-center gap-2">
        <LayoutControls />
        <span className="qc-action-divider" aria-hidden />
        {extraActions}
        <DataQButton type="secondary" onClick={onExport}>
          <FontAwesomeIcon icon={faFileExport} />
          Export
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={onSave}
          isDisabled={isSaving || !dirty}
        >
          <FontAwesomeIcon icon={faFloppyDisk} />
          {isSaving ? "Saving" : "Save"}
        </DataQButton>
      </div>
    </div>
  )
}

export default ActionRow
